import React, { useEffect, useState, useContext } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import queryString from 'query-string'
import axios from 'axios'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import * as md5 from 'md5'
import moment from 'moment-timezone'
import { setDialog } from '../store/actions/dialog'
import FlatPagination from 'material-ui-flat-pagination'
import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Avatar,
  Box,
  TextField,
  Typography,
  Link,
  Badge
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import Title from '../components/atoms/Title'
import CheckBoxes from '../components/atoms/CheckBoxes'
import SelectInputTableHead from '../components/atoms/SelectInputTableHead'
import WebViewModal from '../components/organisms/WebViewModal'
import { User } from '../types/User'
import MessageHistoryModal from '../components/organisms/MessageHistoryModal'
import UserNoteModal from '../components/organisms/UserNoteModal'
import ResetRestrictionLogModal from '../components/organisms/ResetRestrictionLogModal'
import ConfigContext from '../ConfigContext'
import { ExportCsvHelper } from '../ExportCsvHelper'
import CancelIcon from '@material-ui/icons/Close'

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    padding: theme.spacing(2),
    display: 'flex',
    overflow: 'auto',
    flexDirection: 'column',
    height: `100%`
  },
  flexCenter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  modalPaper: {
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3)
  },
  avatar: {
    width: 60,
    height: 60,
    borderRadius: 0
  },
  nowrap: {
    whiteSpace: 'nowrap'
  },
  tableBody: {
    '& .MuiTableCell-sizeSmall': {
      padding: '0.8rem 6px'
    }
  },
  tableHead: {
    '& .MuiTableCell-sizeSmall': {
      padding: '4px 6px'
    }
  },
  table: {
    wordBreak: 'break-all',
    wordWrap: 'break-word'
  },
  badge: {
    '& .MuiBadge-badge': {
      height: 'auto',
      padding: 3,
      borderRadius: '50%',
      backgroundColor: '#FF6363',
      color: '#fff'
    }
  },
  searchLabel: {
    maxWidth: '142px'
  },
  searchLabelMemo: {
    '@media (min-width: 1071px)': {
      maxWidth: '65px'
    }
  },
  searchItem: {
    '@media (min-width: 1056px)': {
      flexGrow: 1,
      maxWidth: 'none',
      flexBasis: 'none'
    }
  },
  searchItemKatudouStatus: {
    '@media (min-width: 1280px)': {
      maxWidth: '230px'
    }
  }
}))

// データ取得apiのクエリー
type QueryString = {
  sort?: string
  order?: 'asc' | 'desc'
  page?: string
  kaiin_no?: string
  name?: string
  affiliation?: string
  limit?: number
  katudou?: string
  profimg?: string
  change?: string
  resetlog?: string
  message?: string
  note?: string
}
// urlのみで保持するクエリー。ページングでのフィルター保持のため
type UrlQueryString = QueryString & {
  katudoufilter?: string
  messagefilter?: string
  notefilter?: string
}
type SearchCheckboxes = { checked: boolean; label: string; value: string }[]

const searchKatudoStatus: SearchCheckboxes = [
  { checked: false, label: '在会', value: '1' },
  { checked: false, label: '退会', value: '0' }
]
const searchMessage: SearchCheckboxes = [
  { checked: false, label: '履歴あり', value: '1' },
  { checked: false, label: '履歴なし', value: '0' }
]
const searchNote: SearchCheckboxes = [
  { checked: false, label: 'メモあり', value: '1' },
  { checked: false, label: 'メモなし', value: '0' }
]
const asQueryString = {
  katudou_status: { init: 'katudou', filter: 'katudoufilter' },
  profile_img: { init: 'profimg', filter: 'profimg' },
  change_count: { init: 'change', filter: 'change' },
  reset_restriction_log: { init: 'resetlog', filter: 'resetlog' },
  message_count: { init: 'message', filter: 'messagefilter' },
  note: { init: 'note', filter: 'notefilter' }
}

const Users: React.FC<RouteComponentProps & ReturnType<typeof mapDispatchToProps>> = ({
  setDialog,
  location,
  history
}) => {
  const initQuery: UrlQueryString = React.useMemo(
    () => queryString.parse(location.search),
    [location.search]
  )

  const classes = useStyles({})
  const [users, setUsers] = useState<
    (User & { reset_restriction_log: number; message_count: number })[]
  >([])
  const [modalState, setModalState] = useState(false)
  const [messageHistoryModalState, setMessageHistoryModalState] = useState({
    kaiinNo: '',
    isOpen: false
  })
  const [userNoteModalState, setUserNoteModalState] = useState({
    kaiinNo: '',
    isOpen: false
  })
  const [resetRestrictionLogModalState, setResetRestrictionLogModalState] = useState({
    kaiinNo: '',
    isOpen: false
  })
  const [webViewUrl, setWebViewUrl] = useState('')
  const [keyword, setKeyword] = useState<{
    kaiin_no: string
    name: string
    affiliation: string
    katudou_status: string
    message_count: string
    note: string
  }>({
    kaiin_no: initQuery.kaiin_no || '',
    name: initQuery.name || '',
    affiliation: initQuery.affiliation || '',
    katudou_status: initQuery.katudou || '',
    message_count: initQuery.message || '',
    note: initQuery.note || ''
  })
  const [filter, setFilter] = useState<{
    katudou_status: string
    profile_img: string
    change_count: string
    reset_restriction_log: string
    message_count: string
    note: string
  }>({
    katudou_status: initQuery.katudoufilter || '',
    profile_img: initQuery.katudoufilter || '',
    change_count: initQuery.change || '',
    reset_restriction_log: initQuery.resetlog || '',
    message_count: initQuery.messagefilter || '',
    note: initQuery.notefilter || ''
  })
  const [total, setTotal] = useState<number>(0)
  const [limit, setLimit] = useState<number>(100)
  const [checkedKatudoStatus, setCheckedKatudoStatus] =
    useState<SearchCheckboxes>(searchKatudoStatus)
  const [checkedMessageCount, setCheckedMessageCount] = useState<SearchCheckboxes>(searchMessage)
  const [checkedNote, setCheckedNote] = useState<SearchCheckboxes>(searchNote)
  const config = useContext(ConfigContext)

  const fetchUsers = async (params: QueryString) => {
    const { data } = await axios.get<{
      number_of_hits: number
      users: (User & { reset_restriction_log: number; message_count: number })[]
    }>('/users/search', {
      params: params
    })
    setUsers(data.users)
    setTotal(data.number_of_hits)
    if (data.number_of_hits > 0) {
      setLimit(100)
    } else {
      setLimit(0)
    }
  }

  useEffect(() => {
    // console.log('初期化処理')
    initQuery.page = '1'
    fetchUsers(initQuery)
  }, [])

  const scrollPageTop = () =>
    document.getElementById('main-content').scroll({
      top: 0,
      left: 0
    })

  const handlePageChange = async (page: number) => {
    try {
      const urlQuery: UrlQueryString = { ...initQuery }
      const query: QueryString = {}
      // ページ遷移するときはクエリストリングで指定された、検索文字を引き継ぐようにする
      // 既存の検索条件をデータ取得用にセット
      for (let queryKey in urlQuery) {
        switch (queryKey) {
          // 活動ステータスフィルター
          case asQueryString.katudou_status.filter:
            query[asQueryString.katudou_status.init] = initQuery[queryKey]
            setFilter({ ...filter, katudou_status: initQuery[queryKey] })
            break
          // メッセージ履歴フィルター
          case asQueryString.message_count.filter:
            query[asQueryString.message_count.init] = initQuery[queryKey]
            setFilter({ ...filter, message_count: initQuery[queryKey] })
            break
          // メモフィルター
          case asQueryString.note.filter:
            query[asQueryString.note.init] = initQuery[queryKey]
            setFilter({ ...filter, note: initQuery[queryKey] })
            break
          // それ以外
          default:
            query[queryKey] = initQuery[queryKey]
            setKeyword({ ...keyword, [queryKey]: initQuery[queryKey] })
            break
        }
      }
      urlQuery.page = String(page)
      query.page = String(page)
      history.push({
        pathname: '/users',
        search: queryString.stringify(urlQuery)
      })
      scrollPageTop()
      await fetchUsers(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleSearch = async (event?: React.FormEvent<HTMLFormElement>) => {
    try {
      event?.preventDefault()
      const query: QueryString = {
        page: '1'
      }
      for (let key in keyword) {
        if (keyword[key]) {
          if (asQueryString[key]) {
            query[asQueryString[key].init] = keyword[key]
          } else {
            query[key] = keyword[key]
          }
        }
      }
      // フィルター表示制御。共通する項目があれば表示を反映する
      setFilter({
        katudou_status: keyword.katudou_status,
        profile_img: '',
        change_count: '',
        reset_restriction_log: '',
        message_count: keyword.message_count,
        note: keyword.note
      })
      history.push({
        pathname: '/users',
        search: queryString.stringify(query)
      })
      scrollPageTop()
      await fetchUsers(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleSearchReset = async () => {
    try {
      history.push({
        pathname: '/users'
      })
      scrollPageTop()
      await fetchUsers({})
      setKeyword({
        kaiin_no: '',
        name: '',
        affiliation: '',
        katudou_status: '',
        message_count: '',
        note: ''
      })
      setCheckedKatudoStatus(searchKatudoStatus)
      setCheckedMessageCount(searchMessage)
      setCheckedNote(searchNote)
      setFilter({
        katudou_status: '',
        profile_img: '',
        change_count: '',
        reset_restriction_log: '',
        message_count: '',
        note: ''
      })
    } catch (error) {
      alert(error.message)
    }
  }

  const handleFilter = async (key: string, select: string) => {
    try {
      const query: QueryString = {}
      const urlQuery: UrlQueryString = { ...initQuery }
      // フィルター解除のとき、既存の検索条件に存在すれば削除する
      if (select === '' && urlQuery[asQueryString[key].filter]) {
        delete urlQuery[asQueryString[key].filter]
      }
      // フィルターと共通の検索キーワードがあればクリア
      if (urlQuery[asQueryString[key].init]) {
        setKeyword({ ...keyword, [key]: '' })
        delete urlQuery[asQueryString[key].init]
      }
      // フィルターと共通の検索チェックボックスもクリア
      switch (asQueryString[key].filter) {
        case asQueryString.katudou_status.filter:
          setCheckedKatudoStatus(searchKatudoStatus)
          break
        case asQueryString.message_count.filter:
          setCheckedMessageCount(searchMessage)
          break
        case asQueryString.note.filter:
          setCheckedNote(searchNote)
          break
        default:
          break
      }
      // 既存の検索条件をデータ取得用にセット
      for (let queryKey in urlQuery) {
        switch (queryKey) {
          case asQueryString.katudou_status.filter:
            query[asQueryString.katudou_status.init] = initQuery[queryKey]
            break
          case asQueryString.message_count.filter:
            query[asQueryString.message_count.init] = initQuery[queryKey]
            break
          case asQueryString.note.filter:
            query[asQueryString.note.init] = initQuery[queryKey]
            break
          default:
            query[queryKey] = initQuery[queryKey]
            break
        }
      }
      if (select) {
        urlQuery[asQueryString[key].filter] = select
        query[asQueryString[key].init] = select
      }
      urlQuery.page = '1'
      query.page = '1'
      history.push({
        pathname: '/users',
        search: queryString.stringify(urlQuery)
      })
      setFilter({ ...filter, [key]: select })
      await fetchUsers(query)
    } catch (error) {
      alert(error.message)
    }
  }

  const handleFilterReset = async () => {
    try {
      // フィルターおよび共通の検索項目はクエリーから除く
      const {
        profimg,
        katudou,
        katudoufilter,
        change,
        resetlog,
        message,
        messagefilter,
        note,
        notefilter,
        ...query
      }: UrlQueryString = initQuery
      history.push({
        pathname: '/users',
        search: queryString.stringify(query)
      })
      await fetchUsers(query)
      // フィルターと共通の検索項目をクリア
      setKeyword({
        ...keyword,
        katudou_status: '',
        message_count: '',
        note: ''
      })
      setCheckedKatudoStatus(searchKatudoStatus)
      setCheckedMessageCount(searchMessage)
      setCheckedNote(searchNote)
      // フィルターをクリア
      setFilter({
        katudou_status: '',
        profile_img: '',
        change_count: '',
        reset_restriction_log: '',
        message_count: '',
        note: ''
      })
    } catch (error) {
      alert(error.message)
    }
  }

  const handleResetRestriction = async (userId: number) => {
    try {
      const user: User = users.filter((user: User) => user.id === userId)[0]
      setDialog(
        '確認',
        `会員番号: ${user.kaiin_no}の端末制限を解除します。よろしいですか？`,
        async () => {
          await axios.post(`/users/reset-restriction/${userId}`)
          feachUsersInitQuery()
        },
        () => {}
      )
    } catch (error) {
      alert(error.message)
    }
  }

  const currentPageCaption = () => {
    const totalNumber = total
    const currentPage = Number(initQuery.page) || 1
    const totalPage = Math.ceil(total / limit)
    const startNumber = totalNumber === 0 ? 0 : (currentPage - 1) * limit + 1
    const lastNumber = currentPage === totalPage ? totalNumber : currentPage * limit
    return (
      <Typography variant="caption" align="center" display="block">
        全 {totalNumber} 件｜ ({startNumber} - {lastNumber}件)
      </Typography>
    )
  }

  const feachUsersInitQuery = async () => {
    const query: QueryString = {}
    const urlQuery: UrlQueryString = { ...initQuery }
    for (let key in initQuery) {
      if (initQuery[key]) {
        switch (key) {
          case asQueryString.katudou_status.filter:
            query[asQueryString.katudou_status.init] = initQuery[key]
            break
          case asQueryString.note.filter:
            query[asQueryString.note.init] = initQuery[key]
            break
          case asQueryString.message_count.filter:
            query[asQueryString.message_count.init] = initQuery[key]
            break
          default:
            query[key] = initQuery[key]
            break
        }
      }
    }
    urlQuery.page = urlQuery.page || '1'
    query.page = urlQuery.page || '1'
    history.push({
      pathname: '/users',
      search: queryString.stringify(urlQuery)
    })
    await fetchUsers(query)
  }

  const exportCSV = async () => {
    console.log('exportCSV total', total)
    console.log('exportCSV keyword', keyword)
    // 検索条件パラメータを指定する
    const query: QueryString & {
      download: string
    } = {
      limit: 10,
      download: '1'
    }
    for (let key in initQuery) {
      if (initQuery[key]) {
        switch (key) {
          case asQueryString.katudou_status.filter:
            query[asQueryString.katudou_status.init] = initQuery[key]
            break
          case asQueryString.note.filter:
            query[asQueryString.note.init] = initQuery[key]
            break
          case asQueryString.message_count.filter:
            query[asQueryString.message_count.init] = initQuery[key]
            break
          default:
            query[key] = initQuery[key]
            break
        }
      }
    }
    // データ取得
    const { data } = await axios.get<{
      number_of_hits: number
      users: (User & { reset_restriction_log: number; message_count: number })[]
    }>('/users/search', {
      params: query
    })
    // CSV出力
    ExportCsvHelper(
      `${config.serviceName}-users`,
      [
        { header: '会員番号', key: 'kaiin_no' },
        { header: '氏名', key: 'name' },
        { header: 'ふりがな', key: 'name_kana' },
        { header: 'メール', key: 'kaiin_email' },
        { header: '所属', key: 'affiliation' },
        { header: '活動ステータス', key: 'katudou_status' }
      ],
      data.users.map(d => {
        return {
          kaiin_no: d.kaiin_no,
          name: `${d.name_last} ${d.name_first}`,
          name_kana: `${d.name_last_kana} ${d.name_first_kana}`,
          kaiin_email: d.kaiin_email,
          affiliation: `${d.affiliation}${d.position ? ' ' + d.position : ''}`,
          katudou_status: d.katudou_status === '1' ? '在会' : '退会'
        }
      })
    )
  }

  return (
    <React.Fragment>
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Title>ユーザー一覧</Title>
          <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <Typography variant="body1" paragraph>
              {config.serviceName}
              の利用登録を完了したユーザー（非会員は除く）のみが検索対象となります。
              <br />
              ※会員番号、氏名は複数ユーザーで検索できます。キーワードは「,」（半角カンマ）区切りで入力・検索してください。
              <br />
              ※氏名の姓名の間のスペースは削除して入力・検索してください。
              <br />
              {config.enableUsersCsvDownload === '1' &&
                '※検索したユーザー情報のCSVファイルをダウンロードすることができます。'}
              {config.micenavi_whats_new !== '' && (
                <div>
                  ※非会員ユーザーは含みません。非会員ユーザー情報は
                  <Link
                    onClick={() => {
                      window.open(
                        `https://www.micenavi.jp/${
                          config.micenavi_whats_new
                        }/admin/auth/process?auth_token=${md5(
                          `${config.micenavi_whats_new}-${moment().format('YYYYMMDDHH')}`
                        )}`,
                        config.micenavi_whats_new
                      )
                    }}
                  >
                    こちら
                  </Link>
                  よりご確認ください。
                </div>
              )}
            </Typography>
            {config.enableUsersCsvDownload === '1' && (
              <Box mx={3} mb="16px">
                <Button
                  variant="contained"
                  color="primary"
                  disabled={total > 0 ? false : true}
                  onClick={() => exportCSV()}
                >
                  CSVダウンロード
                </Button>
              </Box>
            )}
          </Box>

          <Box mb={7}>
            <Paper>
              <Box p={3} mb={3}>
                <form onSubmit={(event: React.FormEvent<HTMLFormElement>) => handleSearch(event)}>
                  <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} sm={2} md={2} lg={2} xl={1} className={classes.searchLabel}>
                      <Typography variant="subtitle2">会員番号</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={10}
                      md={10}
                      lg={10}
                      xl={11}
                      className={classes.searchItem}
                    >
                      <Box>
                        <TextField
                          value={keyword.kaiin_no}
                          variant="outlined"
                          placeholder="会員番号を入力（複数の場合は半角カンマ区切り）"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setKeyword({ ...keyword, kaiin_no: event.target.value })
                          }}
                          fullWidth
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} sm={2} md={2} lg={2} xl={1} className={classes.searchLabel}>
                      <Typography variant="subtitle2">氏名</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={10}
                      md={10}
                      lg={10}
                      xl={11}
                      className={classes.searchItem}
                    >
                      <Box>
                        <TextField
                          value={keyword.name}
                          variant="outlined"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setKeyword({ ...keyword, name: event.target.value })
                          }}
                          fullWidth
                          placeholder="氏名を入力（姓名の間はスペース削除、複数の場合は半角カンマ区切り）"
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container alignItems="center" spacing={3}>
                    <Grid item xs={12} sm={2} md={2} lg={2} xl={1} className={classes.searchLabel}>
                      <Typography variant="subtitle2">所属</Typography>
                    </Grid>
                    <Grid
                      item
                      xs={12}
                      sm={10}
                      md={10}
                      lg={10}
                      xl={11}
                      className={classes.searchItem}
                    >
                      <Box>
                        <TextField
                          value={keyword.affiliation}
                          variant="outlined"
                          placeholder="所属を入力"
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setKeyword({ ...keyword, affiliation: event.target.value })
                          }}
                          fullWidth
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  <Grid container alignItems="center" spacing={3} md={12}>
                    {config.enableUsersSearchKatudouStatus === '1' && (
                      <Grid item xs={2} md={2} lg={2} xl={1} className={classes.searchLabel}>
                        <Typography variant="subtitle2">活動ステータス</Typography>
                      </Grid>
                    )}
                    {config.enableUsersSearchKatudouStatus === '1' && (
                      <Grid
                        item
                        xs={10}
                        md={2}
                        lg={2}
                        xl={2}
                        justify="flex-start"
                        className={classes.searchItemKatudouStatus}
                      >
                        <CheckBoxes
                          options={checkedKatudoStatus}
                          chekedHandler={checked => {
                            setCheckedKatudoStatus(checked)
                            let checkedTrue = checked.filter(c => c.checked === true)
                            if (checkedTrue.length === 1) {
                              setKeyword({ ...keyword, katudou_status: checkedTrue[0].value })
                            } else {
                              setKeyword({ ...keyword, katudou_status: '' })
                            }
                          }}
                        />
                      </Grid>
                    )}
                    {config.enableUsersSearchMessageCount === '1' && (
                      <Grid item xs={2} md={2} lg={2} xl={1} className={classes.searchLabel}>
                        <Typography variant="subtitle2">メッセージ履歴</Typography>
                      </Grid>
                    )}
                    {config.enableUsersSearchMessageCount === '1' && (
                      <Grid item xs={10} md={true} lg={2} xl={2} justify="flex-start">
                        <CheckBoxes
                          options={checkedMessageCount}
                          chekedHandler={checked => {
                            setCheckedMessageCount(checked)
                            let checkedTrue = checked.filter(c => c.checked === true)
                            if (checkedTrue.length === 1) {
                              setKeyword({ ...keyword, message_count: checkedTrue[0].value })
                            } else {
                              setKeyword({ ...keyword, message_count: '' })
                            }
                          }}
                        />
                      </Grid>
                    )}
                    {config.enableUsersSearchNote === '1' && (
                      <Grid item xs={2} md={1} lg={1} xl={1} className={classes.searchLabelMemo}>
                        <Typography variant="subtitle2">メモ</Typography>
                      </Grid>
                    )}
                    {config.enableUsersSearchNote === '1' && (
                      <Grid item xs={10} md={true} lg={2} xl={2} justify="flex-start">
                        <CheckBoxes
                          options={checkedNote}
                          chekedHandler={checked => {
                            setCheckedNote(checked)
                            let checkedTrue = checked.filter(c => c.checked === true)
                            if (checkedTrue.length === 1) {
                              setKeyword({ ...keyword, note: checkedTrue[0].value })
                            } else {
                              setKeyword({ ...keyword, note: '' })
                            }
                          }}
                        />
                      </Grid>
                    )}
                  </Grid>
                  <Box mt={3} display="flex" justifyContent="flex-end" alignItems="center">
                    <Box mr={3}>
                      <Button variant="contained" color="primary" onClick={handleSearchReset}>
                        リセット
                      </Button>
                    </Box>
                    <Box>
                      <Button type="submit" variant="contained" color="primary">
                        検索
                      </Button>
                    </Box>
                  </Box>
                </form>
              </Box>
            </Paper>
          </Box>

          {config.enableUsersFilterReset === '1' && (
            <Box display="flex" justifyContent="flex-end" alignItems="center" mt={5} mr={2}>
              <Badge
                className={classes.badge}
                badgeContent={<CancelIcon fontSize="small" />}
                invisible={Object.values(filter).find(f => f !== '') ? false : true}
                max={0}
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleFilterReset}
                  disabled={Object.values(filter).find(f => f !== '') ? false : true}
                >
                  フィルターを解除
                </Button>
              </Badge>
            </Box>
          )}

          <Box>
            <Box display="flex" justifyContent="center">
              {currentPageCaption}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={limit}
                offset={(Number(initQuery.page || 1) - 1) * limit}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
          <Table size="small" className={classes.table}>
            <TableHead className={classes.tableHead}>
              <TableRow>
                {/* <TableCell align="center">
                  {config.enableUsersFilterProfileImg === '1' ? (
                    <SelectInputTableHead
                      value={filter.profile_img}
                      label={'画像'}
                      selectHandler={(select: string) => handleFilter('profile_img', select)}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '1', label: '画像あり' },
                        { value: '0', label: '画像なし' }
                      ]}
                    />
                  ) : (
                    '画像'
                  )}
                </TableCell> */}
                <TableCell>会員番号</TableCell>
                <TableCell>氏名</TableCell>
                <TableCell>メールアドレス</TableCell>
                <TableCell>所属</TableCell>
                <TableCell>
                  {config.enableUsersFilterKatudouStatus === '1' ? (
                    <SelectInputTableHead
                      value={filter.katudou_status}
                      label={'活動ステータス'}
                      selectHandler={(select: string) => {
                        if (keyword.katudou_status) {
                          setKeyword({ ...keyword, katudou_status: '' })
                          setCheckedKatudoStatus(searchKatudoStatus)
                        }
                        handleFilter('katudou_status', select)
                      }}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '1', label: '在会' },
                        { value: '0', label: '退会' }
                      ]}
                    />
                  ) : (
                    '活動ステータス'
                  )}
                </TableCell>
                {/* <TableCell align="center">
                  <Box>
                    {config.enableUsersFilterChangeCount === '1' ? (
                      <SelectInputTableHead
                        value={filter.change_count}
                        label={'端末変更制限'}
                        selectHandler={(select: string) => handleFilter('change_count', select)}
                        options={[
                          { value: '', label: 'すべて表示' },
                          { value: '1', label: '変更制限あり' },
                          { value: '0', label: '変更制限なし' }
                        ]}
                      />
                    ) : (
                      '端末変更制限'
                    )}
                    {config.enableUsersFilterResetRestrictionLog === '1' ? (
                      <SelectInputTableHead
                        value={filter.reset_restriction_log}
                        label={'端末解除履歴'}
                        selectHandler={(select: string) =>
                          handleFilter('reset_restriction_log', select)
                        }
                        options={[
                          { value: '', label: 'すべて表示' },
                          { value: '1', label: '解除履歴あり' },
                          { value: '0', label: '解除履歴なし' }
                        ]}
                      />
                    ) : (
                      ''
                    )}
                  </Box>
                </TableCell> */}
                {/* <TableCell align="center">
                  <div>プロフィール</div>
                  <div>ページ</div>
                </TableCell> */}
                <TableCell align="center">
                  {config.enableUsersFilterMessageCount === '1' ? (
                    <SelectInputTableHead
                      value={filter.message_count}
                      label={'メッセージ履歴'}
                      selectHandler={(select: string) => {
                        if (keyword.message_count) {
                          setKeyword({ ...keyword, message_count: '' })
                          setCheckedMessageCount(searchMessage)
                        }
                        handleFilter('message_count', select)
                      }}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '1', label: 'メッセージ履歴あり' },
                        { value: '0', label: 'メッセージ履歴なし' }
                      ]}
                    />
                  ) : (
                    'メッセージ履歴'
                  )}
                </TableCell>
                <TableCell align="center">
                  {config.enableUsersFilterNote === '1' ? (
                    <SelectInputTableHead
                      value={filter.note}
                      label={'メモ'}
                      selectHandler={(select: string) => {
                        if (keyword.note) {
                          setKeyword({ ...keyword, note: '' })
                          setCheckedNote(searchNote)
                        }
                        handleFilter('note', select)
                      }}
                      options={[
                        { value: '', label: 'すべて表示' },
                        { value: '1', label: 'メモあり' },
                        { value: '0', label: 'メモなし' }
                      ]}
                    />
                  ) : (
                    'メモ'
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {users.map((user, index) => (
                <TableRow key={user.id}>
                  {/* <TableCell component="th" scope="row">
                    <Box display="flex" justifyContent="center">
                      <Avatar
                        src={`${user.profile_img}`}
                        alt={user.profile_img}
                        className={classes.avatar}
                      />
                    </Box>
                  </TableCell> */}
                  <TableCell align="left">{user.kaiin_no}</TableCell>
                  <TableCell align="left" className={classes.nowrap}>
                    <div>{`${user.name_last} ${user.name_first}`}</div>
                    {user.name_last_kana && (
                      <div>{`（${user.name_last_kana} ${user.name_first_kana}）`}</div>
                    )}
                  </TableCell>
                  <TableCell align="left">{user.kaiin_email}</TableCell>
                  <TableCell align="left">{`${user.affiliation} ${user.position}`}</TableCell>
                  <TableCell align="center">
                    {user.katudou_status === '1' ? '在会' : '退会'}
                  </TableCell>
                  {/* <TableCell align="center">
                    <div className={classes.flexCenter}>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleResetRestriction(user.id)}
                        disabled={user.change_count === 0}
                      >
                        解除
                      </Button>
                      <Button
                        variant="contained"
                        style={{ marginLeft: 3 }}
                        onClick={() => {
                          setResetRestrictionLogModalState({ isOpen: true, kaiinNo: user.kaiin_no })
                        }}
                        color="primary"
                        disabled={user.reset_restriction_log === 0}
                      >
                        解除履歴
                      </Button>
                    </div>
                  </TableCell> */}
                  {/* <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setModalState(true)
                        setWebViewUrl(
                          `${axios.defaults.baseURL}/previews/user-profile?userid=${user.id}`
                        )
                      }}
                    >
                      表示
                    </Button>
                  </TableCell> */}
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        setMessageHistoryModalState({ isOpen: true, kaiinNo: user.kaiin_no })
                      }}
                      disabled={!user.message_count && true}
                    >
                      メッセージ履歴
                    </Button>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color={user.note ? 'primary' : 'default'}
                      onClick={() => {
                        setUserNoteModalState({ isOpen: true, kaiinNo: user.kaiin_no })
                      }}
                    >
                      メモ
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Box my={4}>
            <Box display="flex" justifyContent="center">
              {currentPageCaption}
            </Box>
            <Box display="flex" justifyContent="center">
              <FlatPagination
                limit={limit}
                offset={(Number(initQuery.page || 1) - 1) * limit}
                total={total}
                onClick={(ev, offset, _page) => {
                  handlePageChange(_page)
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Grid>
      <WebViewModal isOpen={modalState} handleClose={() => setModalState(false)} url={webViewUrl} />
      <MessageHistoryModal
        isOpen={messageHistoryModalState.isOpen}
        closeHandler={() =>
          setMessageHistoryModalState({ ...messageHistoryModalState, isOpen: false })
        }
        kaiinNo={messageHistoryModalState.kaiinNo}
      />
      <UserNoteModal
        kaiinNo={userNoteModalState.kaiinNo}
        isOpen={userNoteModalState.isOpen}
        closeHandler={() => {
          setUserNoteModalState({ kaiinNo: '', isOpen: false })
          feachUsersInitQuery()
        }}
      />
      <ResetRestrictionLogModal
        kaiinNo={resetRestrictionLogModalState.kaiinNo}
        isOpen={resetRestrictionLogModalState.isOpen}
        closeHandler={() => setResetRestrictionLogModalState({ kaiinNo: '', isOpen: false })}
      />
    </React.Fragment>
  )
}

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    setDialog: (title: string, content: string, okHandler: () => void, ngHandler: () => void) =>
      dispatch(setDialog(title, content, okHandler, ngHandler))
  }
}
export default connect(null, mapDispatchToProps)(Users)
